/**
 * RAM — Reactivate Dormant (supervisor-approval flavor).
 *
 * POST /v3/maint/acct/reactivate-dormant
 *
 * Distinct from the standard `DormantRelease` action: this flavor always
 * routes to a Supervisor for approval. Customer-presence verification
 * (in-branch, ID-checked) is recommended before submitting.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import { asIso8601 } from '@retail-ops/shared-types';
import type { RamTarget } from '../types';

export interface ReactivateDormantProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function ReactivateDormant({
  target,
  onDone,
  onCancel,
}: ReactivateDormantProps): JSX.Element {
  const client = useApiClient();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [lastContactDate, setLastContactDate] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const canSave =
    !submitting && Boolean(target.acctId) && reason.trim().length >= 10;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitReactivateDormant(b),
      {
        accountNumber: target.acctId,
        lastContactDate: lastContactDate ? asIso8601(lastContactDate) : undefined,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'Supervisor'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          Dormant reactivation submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Reactivate dormant account</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Reactivation requires Supervisor approval. Customer-presence verification (in-branch, ID-checked) is recommended before submitting.
      </Banner>

      <div className="ram-wizard__fields">
        <label className="ram-search__label">
          Last customer-contact date
          <input
            className="ram-input"
            type="date"
            value={lastContactDate}
            onChange={(e) => setLastContactDate(e.target.value)}
          />
        </label>
        <label className="ram-search__label">
          Reason (required, min 10 chars)
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : 'Submit reactivation'}
        </Button>
      </div>
    </div>
  );
}
